import { AreaChartDataSet, AreaSeriesColor } from '../types';
import { AREA_CHART_COLORS } from './areaChartColors';

const omit = (obj: Record<string, unknown>, keys: string[]): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    if (!keys.includes(key)) result[key] = obj[key];
  }
  return result;
};

// ─── Colour helpers ───────────────────────────────────────────────────────────

/**
 * Converts a 3- or 6-digit hex colour string to an rgba() value.
 * Falls back to a semi-transparent black if the input cannot be parsed.
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  const sanitised = hex.replace('#', '');
  const expanded =
    sanitised.length === 3
      ? sanitised
          .split('')
          .map((c) => c + c)
          .join('')
      : sanitised;

  const r = parseInt(expanded.substring(0, 2), 16);
  const g = parseInt(expanded.substring(2, 4), 16);
  const b = parseInt(expanded.substring(4, 6), 16);

  if ([r, g, b].some(isNaN)) return `rgba(0,0,0,${alpha})`;

  return `rgba(${r},${g},${b},${alpha})`;
};

// ─── Dataset builder ──────────────────────────────────────────────────────────

interface BuildDatasetsOptions {
  colorConfig: AreaSeriesColor[];
  stacked: boolean;
  tension: number;
  showPoints: boolean;
  pointRadius: number;
  fillOpacity: number;
  /** Whether to draw a visible stroke on each line. Default false (fill only). */
  showStroke: boolean;
}

/**
 * Pure function — safe to use directly as a useMemo factory.
 *
 * Converts an AreaChartDataSet into Chart.js dataset objects:
 * - Overlaid mode: every series fills to 'origin'
 * - Stacked mode:  index 0 fills to 'origin'; subsequent series fill to '-1'
 *   (Chart.js stacks fills when `scales.y.stacked` is also true)
 */
export interface BuildDatasetsResult {
  datasets: ReturnType<typeof buildDatasetsInternal>;
  /** Pre-computed sum of all series values per column label, e.g. { "2025-02": 12345 }. */
  totalMap: Record<string, number>;
}

function buildDatasetsInternal(
  data: AreaChartDataSet,
  columns: string[],
  options: BuildDatasetsOptions,
) {
  const { colorConfig, stacked, tension, showPoints, pointRadius, fillOpacity, showStroke } =
    options;

  const ordered = stacked
    ? [...data].sort((a, b) => {
        const sumA = a.points.reduce((s, p) => s + (p.y ?? 0), 0);
        const sumB = b.points.reduce((s, p) => s + (p.y ?? 0), 0);
        return sumB - sumA;
      })
    : data;

  return ordered.map((series, index) => {
    const color = colorConfig[index % colorConfig.length] ?? AREA_CHART_COLORS[0];
    const fill = stacked && index > 0 ? '-1' : 'origin';

    const pointMap = new Map(series.points.map((p) => [p.x, p.y]));

    let values: (number | null)[];
    if (stacked) {
      const firstIdx = columns.findIndex((col) => pointMap.has(col));
      let lastIdx = -1;
      for (let i = columns.length - 1; i >= 0; i--) {
        if (pointMap.has(columns[i])) {
          lastIdx = i;
          break;
        }
      }
      values = columns.map((col, i) => {
        if (i < firstIdx || firstIdx === -1) return null;
        if (i > lastIdx) return null;
        return pointMap.get(col) ?? 0;
      });
    } else {
      values = columns.map((col) => pointMap.get(col) ?? null);
    }

    return {
      label: series.name,
      data: values,
      borderColor: color,
      backgroundColor: hexToRgba(color, fillOpacity),
      fill,
      tension,
      pointRadius: values.map((v) => (v == null || v === 0 ? 0 : showPoints ? pointRadius : 0)),
      pointHoverRadius: values.map((v) =>
        v == null || v === 0 ? 0 : showPoints ? pointRadius + 2 : 4,
      ),
      pointHitRadius: values.map((v) => (v == null || v === 0 ? 0 : 8)),
      borderWidth: showStroke ? 2 : 0,
    };
  });
}

/**
 * Sorts series by total contribution (descending) for stacked charts.
 */
export const sortSeriesByTotal = (data: AreaChartDataSet): AreaChartDataSet =>
  [...data].sort((a, b) => {
    const sumA = a.points.reduce((s, p) => s + (p.y ?? 0), 0);
    const sumB = b.points.reduce((s, p) => s + (p.y ?? 0), 0);
    return sumB - sumA;
  });

/**
 * Builds a map of column label to the sum of all series values at that column.
 */
export const buildTotalMap = (
  data: AreaChartDataSet,
  columns: string[],
): Record<string, number> => {
  const totalMap: Record<string, number> = {};
  for (const col of columns) {
    let sum = 0;
    for (const series of data) {
      const pt = series.points.find((p) => p.x === col);
      if (pt) sum += pt.y ?? 0;
    }
    totalMap[col] = sum;
  }
  return totalMap;
};

export const buildDatasets = (
  data: AreaChartDataSet,
  columns: string[],
  options: BuildDatasetsOptions,
): BuildDatasetsResult => {
  const datasets = buildDatasetsInternal(data, columns, options);
  const totalMap = buildTotalMap(data, columns);

  return { datasets, totalMap };
};

// ─── Layout constants ─────────────────────────────────────────────────────────

const LAYOUT_PADDING = { bottom: 36, leftYAxis: 44 };

// ─── Options builder ──────────────────────────────────────────────────────────

interface BuildChartOptionsParams {
  stacked: boolean;
  yAxisFormatter: (value: number | string) => string;
  xAxisFormatter: (label: string) => string;
  /** External tooltip handler from useChartTooltip. Replaces built-in canvas tooltips. */
  tooltip: (context: any) => void;
  chartOptions: Record<string, unknown>;
  yAxisLabel?: string;
  /** Labels along the category (x) axis — must match what `<Line>` receives as `labels`. */
  categoryLabels: string[];
  /** First visible category index. When set together with viewportMax, the x-axis is clipped. */
  viewportMin?: number;
  /** Last visible category index. */
  viewportMax?: number;
}

/**
 * Pure function — safe to use directly as a useMemo factory.
 *
 * Builds the full Chart.js options object. The caller's `chartOptions` are
 * shallow-merged at the plugins, scales, and root levels so that individual
 * overrides do not wipe out defaults.
 *
 * Viewport (viewportMin/viewportMax) is set directly in the options object so
 * that react-chartjs-2 always owns the full options and never overwrites the
 * visible range on re-render.
 */
export const buildChartOptions = ({
  stacked,
  yAxisFormatter,
  xAxisFormatter,
  tooltip,
  chartOptions,
  yAxisLabel,
  categoryLabels,
  viewportMin,
  viewportMax,
}: BuildChartOptionsParams): Record<string, unknown> => {
  const visibleCount =
    viewportMin !== undefined && viewportMax !== undefined
      ? viewportMax - viewportMin + 1
      : categoryLabels.length;
  const maxTicksLimit = Math.min(12, Math.max(1, visibleCount));

  const baseOptions: Record<string, unknown> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
    layout: {
      padding: {
        bottom: LAYOUT_PADDING.bottom,
        ...(yAxisLabel && { left: LAYOUT_PADDING.leftYAxis }),
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        mode: 'nearest',
        intersect: false,
        external: tooltip,
        ...(chartOptions.tooltip as object | undefined),
      },
      ...(chartOptions.plugins as object | undefined),
    },
    scales: {
      x: {
        grid: { display: true, color: '#AECBD0' },
        border: { color: '#E8EAEB', dash: [4, 4] },
        ...(viewportMin !== undefined && { min: viewportMin }),
        ...(viewportMax !== undefined && { max: viewportMax }),
        ticks: {
          callback: (tickValue: number | string) => {
            const idx =
              typeof tickValue === 'number' ? tickValue : categoryLabels.indexOf(String(tickValue));
            const label = categoryLabels[idx] ?? String(tickValue);
            return xAxisFormatter(label);
          },
          color: '#898C8D',
          font: { family: 'Manrope', size: 11, weight: '600' },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit,
        },
      },
      y: {
        stacked,
        beginAtZero: true,
        grid: {
          display: true,
          color: '#AECBD0',
        },
        border: { color: '#E8EAEB', dash: [4, 4] },
        ticks: {
          callback: (value: number | string) => yAxisFormatter(value),
          color: '#898C8D',
          font: { family: 'Manrope', size: 11, weight: '600' },
        },
        ...((chartOptions.scales as Record<string, unknown> | undefined)?.y as object | undefined),
      },
      ...((chartOptions.scales as Record<string, unknown> | undefined) ?? {}),
    },
    ...omit(chartOptions, ['plugins', 'scales', 'tooltip']),
  };

  return baseOptions;
};
